import { IntlShape } from '@redbubble/next/intl';
import {
  calendarDateRange,
  formatDate,
  formatMonthFirstDate,
} from '../../../pages/cart/components/OrderSummary/Shipping/utils';
import type {
  DeliveryDetail,
  DeliveryTimeFrame,
  LastOrderByDate,
} from '../types';
import { DeliveryTimeFrameType } from '../types';
import { messages } from './messages';

export type Formatter = (str) => JSX.Element;

const timeFrameMessage = (
  { earliest, latest }: DeliveryTimeFrame,
  { locale, formatMessage }: IntlShape
) => {
  const isRange = earliest !== latest;

  if (isRange) {
    const earliestFormatted = formatDate(earliest, locale);
    const latestFormatted = formatDate(latest, locale);

    return formatMessage(messages.dateRange, {
      dateRange: calendarDateRange(earliestFormatted, latestFormatted),
    });
  }

  const earliestFormatted = formatMonthFirstDate(earliest, locale);

  return formatMessage(messages.dateBy, {
    date: earliestFormatted,
  });
};

const lastOrderByDateMessage = (
  { targetDate, daysLeft }: LastOrderByDate,
  { locale, formatMessage }: IntlShape,
  bold: Formatter
) => {
  const isToday = daysLeft <= 1;

  const formattedTargetDate = formatDate(targetDate, locale);

  if (isToday) {
    return formatMessage(messages.orderToday, {
      targetDate: formattedTargetDate,
      bold,
    });
  }

  return formatMessage(messages.daysLeft, {
    number: daysLeft,
    targetDate: formattedTargetDate,
    bold,
  });
};

// NOTE (JJ): do not apply any styling unless the consumer requires it
const emptyBoldFormatter: Formatter = (str) => str as JSX.Element;

export const deliveryWindowMessage = (
  deliveryDetail: DeliveryDetail,
  intl: IntlShape,
  bold: Formatter = emptyBoldFormatter
) => {
  if (deliveryDetail.type === DeliveryTimeFrameType.DeliveryTimeFrame)
    return timeFrameMessage(deliveryDetail, intl);

  return lastOrderByDateMessage(deliveryDetail, intl, bold);
};
